import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export const useRFQs = (filters = {}) => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRFQs();
  }, [JSON.stringify(filters)]);

  const fetchRFQs = async () => {
    try {
      setLoading(true);

      // Supabase/PostgREST caps an unranged select at 1000 rows by default -
      // with 2000+ RFQs that silently truncated the list. Page through with
      // .range() until a page comes back short, instead of raising the cap
      // (which just moves the ceiling, it doesn't remove it).
      const PAGE_SIZE = 1000;
      const buildPage = (from, to) => {
        let query = supabase
          .from('rfqs')
          .select(`
            *,
            client:clients(name),
            applications(status)
          `)
          .order('created_at', { ascending: false })
          .range(from, to);

        // Apply filters
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        if (filters.client_id) {
          query = query.eq('client_id', filters.client_id);
        }
        if (filters.search) {
          query = query.or(`ref_code.ilike.%${filters.search}%,title.ilike.%${filters.search}%`);
        }

        return query;
      };

      let data = [];
      let from = 0;
      while (true) {
        const { data: page, error } = await buildPage(from, from + PAGE_SIZE - 1);
        if (error) throw error;
        data = data.concat(page || []);
        if (!page || page.length < PAGE_SIZE) break;
        from += PAGE_SIZE;
      }

      setRfqs(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching RFQs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { rfqs, loading, error, refetch: fetchRFQs };
};

export const useRFQ = (rfqId) => {
  const [rfq, setRfq] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (rfqId) {
      fetchRFQDetails();
    }
  }, [rfqId]);

  const fetchRFQDetails = async () => {
    try {
      setLoading(true);

      // Fetch RFQ with client info
      const { data: rfqData, error: rfqError } = await supabase
        .from('rfqs')
        .select(`
          *,
          client:clients(*)
        `)
        .eq('id', rfqId)
        .single();

      if (rfqError) throw rfqError;

      // Fetch applications with candidate details
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('applications')
        .select(`
          *,
          candidate:candidates(first_name, last_name, email, phone, clearance_level, location_city, location_state)
        `)
        .eq('rfq_id', rfqId);

      if (applicationsError) throw applicationsError;

      setRfq(rfqData);
      setApplications(applicationsData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching RFQ details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { rfq, applications, loading, error, refetch: fetchRFQDetails };
};
